<template>
  <div class="login">
    <div class="login__wrapper">
      <div class="login-body">
        <div class="login-body__header">
          <img src="@/assets/images/logo.svg" alt="logo" style="width: 315px" />
        </div>
        <div class="login-body__main">
          <input
            v-model.trim="dataUser.login"
            type="text"
            class="login-body__main_login"
            placeholder="Логин"
            maxlength="50"
            required
          />
          <input
            v-model.trim="dataUser.password"
            type="password"
            class="login-body__main_password"
            placeholder="Пароль"
            minlength="6"
            maxlength="40"
            required
          />
          <label class="login__agreement">
            <input
              type="checkbox"
              v-model="isAgreed"
              class="login__agreement-checkbox"
            />
            <span class="login__agreement-text">
              Я даю
              <a
                href="https://monoclick.io/soglasie"
                target="_blank"
                rel="noopener noreferrer"
                class="login__agreement-link"
                @click.stop
              >
                согласие
              </a>
              на обработку персональных данных на условиях
              <a
                href="https://monoclick.io/politica"
                target="_blank"
                rel="noopener noreferrer"
                class="login__agreement-link"
                @click.stop
              >
                Политики обработки персональных данных ООО "Альянс Юг"
              </a>
            </span>
          </label>
        </div>
        <div class="login-body__buttons">
          <button
            class="login-body__buttons_accept"
            :class="{ disabled: isSubmitting || !isFormValid }"
            :disabled="isSubmitting || !isFormValid"
            @click="isAgreed && autUser()"
          >
            Дать газу...
          </button>
        </div>
      </div>
    </div>
    <ModalLoginError
      v-if="modalLoginError"
      @closeModalLoginError="closeModalLoginError"
      :textError="textError"
    />
  </div>
</template>

<script setup>
import ModalLoginError from "@/components/Modals/ModalLoginError.vue";
import { useApi } from "@/composition/useApi.js";
import { useLogin } from "@/composition/useLogin.js";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import config from "/config.json";

const { loginUser, authUser } = useLogin();
const { tokenCSRF } = useApi();
const route = useRoute();

const dataUser = ref({
  login: "",
  password: "",
});

const isAgreed = ref(false);
const isSubmitting = ref(false);
const textError = ref(null);
const modalLoginError = ref(false);
const headers = ref({});
const isAuth = ref(true);

const isFormValid = computed(() => {
  return (
    dataUser.value.login.trim().length > 0 &&
    dataUser.value.password.trim().length > 0 &&
    isAgreed.value
  );
});

const closeModalLoginError = () => (modalLoginError.value = false);

const sendRequestCookies = () => {
  const xhr = new XMLHttpRequest();
  xhr.onload = () => {
    tokenCSRF.value = JSON.parse(xhr.response).token;
    headers.value = { "csrf-token": tokenCSRF.value };
  };
  xhr.open("GET", `${config.API_URL}/v1/csrf/`, true);
  xhr.withCredentials = true;
  xhr.send(null);
};

const autUser = () => {
  if (!isFormValid.value || isSubmitting.value) return;
  isSubmitting.value = true;

  loginUser(dataUser.value, {
    withCredentials: true,
    headers: headers.value,
  })
    .then(() => {
      const redirectUrl = route.query.redirect || config.REDIRECT_URL;
      const redirectDomain = new URL(redirectUrl).hostname;
      window.location.href = config.ALLOWED_DOMAINS.includes(redirectDomain)
        ? redirectUrl
        : config.REDIRECT_URL;
    })
    .catch(e => {
      if (e.response?.data?.statusCode === 402)
        textError.value = e.response.data.message;
      tokenCSRF.value = undefined;
      modalLoginError.value = true;
    })
    .finally(() => {
      isSubmitting.value = false;
    });
};

onMounted(() => {
  sendRequestCookies();
  document.addEventListener("keydown", e => {
    if (e.code === "Enter" && isAgreed.value) autUser();
  });
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", autUser);
});

watch(() => {
  if (tokenCSRF.value) {
    if (isAuth.value)
      authUser(tokenCSRF.value).then(r => {
        if (r.status === 200) window.location.href = config.REDIRECT_URL;
      });
    isAuth.value = false;
    setTimeout(() => sendRequestCookies(), 210000);
  }
});
</script>

<style scoped lang="scss">
.login {
  position: absolute;
  background-image: url("~@/assets/images/map.png");
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;

  &__agreement {
    display: flex;
    align-items: center;
    gap: 20px;
    width: 100%;
    margin-top: 10px;

    &-checkbox {
      appearance: none;
      width: 22px;
      height: 22px;
      border: 1.2px solid #e2e3ed;
      border-radius: 4px;
      background-color: #fff;
      cursor: pointer;
      position: relative;
      transition: all 0.2s ease;

      &:checked {
        background-color: #e21e23;
        border-color: #e21e23;
      }

      &:checked::after {
        content: "";
        position: absolute;
        left: 6px;
        top: 3px;
        width: 5px;
        height: 9px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &-text {
      flex: 1;
      text-align: left;
      color: #757575;
      font-size: 14px;
      line-height: 1.4;
    }

    &-link {
      color: #e21e23;
      text-decoration: underline;
      transition: color 0.2s ease;

      &:hover {
        color: lighten(#e21e23, 10%);
      }
    }
  }
}

.login-body {
  padding: 20px;
  width: 320px;
  margin-top: -40px;

  &__header {
    &_title {
      color: #000000;
      font-size: 28px;
    }
  }

  &__main {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: 20px;

    &_login {
      font-size: large;
      padding: 10px 20px;
      width: calc(100% - 40px);
      height: 35px;
      border-radius: 15px;
      border: 1px solid #e1e3ed;
    }

    &_password {
      font-size: large;
      margin-top: 10px;
      padding: 10px 20px;
      width: calc(100% - 40px);
      height: 35px;
      border-radius: 15px;
      border: 1px solid #e1e3ed;
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: 20px;

    &_accept {
      height: auto;
      border-radius: 15px;
      background: #da2e35;
      color: #ffffff;
      cursor: pointer;
      font-size: large;
      padding: 10px 20px;
      width: 100%;
      border: 1px solid #e1e3ed;
      text-align: center;
      line-height: 30px;
      transition: background 0.2s ease;

      &:hover:not(.disabled) {
        background: #c41e25;
      }

      &:disabled,
      &.disabled {
        background: #cccccc;
        color: #666666;
        cursor: not-allowed;
        border: 1px solid #b3b3b3;
      }
    }
  }
}
</style>
